import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, type Board as BoardData, type Card, type Filters, type Me, type Schema, type SyncState } from './api.js'
import { Board, CardTile } from './Board.jsx'
import { CardPanel } from './CardPanel.jsx'
import { Login } from './Login.jsx'

type Tab = 'доска' | 'очередь' | 'архив' | 'библиотека'

export function App() {
  const [me, setMe] = useState<Me | null>(null)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    api
      .me()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setChecked(true))
  }, [])

  if (!checked) return null
  if (!me) return <Login onDone={() => void api.me().then(setMe)} />
  return <Workspace me={me} onLogout={() => { void api.logout(); setMe(null) }} />
}

function Workspace({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const [schema, setSchema] = useState<Schema | null>(null)
  const [tab, setTab] = useState<Tab>('доска')
  const [filters, setFilters] = useState<Filters>({})
  const [board, setBoard] = useState<BoardData | null>(null)
  const [list, setList] = useState<Card[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [sync, setSync] = useState<SyncState | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    api.schema().then(setSchema).catch(() => setError('Не удалось прочитать словари из vault\'а'))
    void refreshSync()
  }, [])

  const refreshSync = useCallback(async () => {
    try {
      setSync(await api.sync())
    } catch {
      // состояние синхронизации не критично для работы
    }
  }, [])

  const reload = useCallback(async () => {
    setError('')
    try {
      if (tab === 'доска') setBoard(await api.board(filters))
      else if (tab === 'архив') setList(await api.archive(filters))
      else if (tab === 'очередь') setList(await api.queue())
      else setList(await api.library({ ...filters, limit: 200 }))
      void refreshSync()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить')
    }
  }, [tab, filters, refreshSync])

  useEffect(() => {
    void reload()
  }, [reload])

  if (!schema) {
    return <div className="login"><p className="muted">{error || 'Загружаю словари…'}</p></div>
  }

  const cards = tab === 'доска' ? (board?.cards ?? []) : list

  return (
    <div className="app">
      <div className="topbar">
        <span className="brand">CRM контента</span>
        <nav className="tabs">
          {(['доска', 'очередь', 'архив', 'библиотека'] as Tab[]).map((t) => (
            <button key={t} className={tab === t ? 'active small' : 'small'} onClick={() => { setTab(t); setSelected(null) }}>
              {t === 'очередь' ? 'ждут доработки' : t}
            </button>
          ))}
        </nav>
        <div className="grow" />
        <button className="small primary" onClick={() => setAdding(true)}>+ Входящее</button>
        <button
          className="small ghost"
          disabled={busy}
          title="Перечитать весь vault — за Библиотекой не следим в реальном времени"
          onClick={async () => {
            setBusy(true)
            try {
              await api.reindex()
              await reload()
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? 'Обновляю…' : 'Обновить библиотеку'}
        </button>
        <button
          className="small ghost"
          title="Сбросить подсветку «новое с прошлого входа»"
          onClick={async () => {
            await api.markSeen()
            await reload()
          }}
        >
          Всё просмотрено
        </button>
        <span className="small muted">{me.email}</span>
        <button className="small ghost" onClick={onLogout}>Выйти</button>
      </div>

      {!me.mailConfigured && (
        <div className="banner">
          <span className="warn">⚠</span>
          <span>
            Почта не настроена: коды входа печатаются в лог сервера. Для работы командой заполните
            <code> SMTP_*</code> в <code>.env</code>.
          </span>
        </div>
      )}
      {sync?.lastError && (
        <div className="banner">
          <span className="warn">⚠</span>
          <span>Синхронизация с Gitea отстала: {sync.lastError}. Файлы сохранены.</span>
          <button className="small" onClick={() => void api.retrySync().then(setSync)}>Повторить</button>
        </div>
      )}

      <div className="filters">
        <input
          placeholder="Поиск по тексту…"
          defaultValue={filters.query ?? ''}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setFilters((f) => ({ ...f, query: (e.target as HTMLInputElement).value }))
          }}
          style={{ minWidth: 220 }}
        />
        <Select
          value={filters.format ?? ''}
          onChange={(v) => setFilters((f) => ({ ...f, format: v }))}
          placeholder="формат"
          options={schema.formats.map((x) => [x.ключ, x.название])}
        />
        <Select
          value={filters.rubric ?? ''}
          onChange={(v) => setFilters((f) => ({ ...f, rubric: v }))}
          placeholder="рубрика"
          options={schema.rubrics.map((x) => [x.ключ, x.название])}
        />
        <Select
          value={filters.direction ?? ''}
          onChange={(v) => setFilters((f) => ({ ...f, direction: v }))}
          placeholder="направление"
          options={schema.directions.map((x) => [x.ключ, x.название])}
        />
        <Select
          value={filters.expert ?? ''}
          onChange={(v) => setFilters((f) => ({ ...f, expert: v }))}
          placeholder="эксперт"
          options={schema.experts.map((x) => [x, x])}
        />
        <label className="row small" style={{ margin: 0, gap: 6 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={filters.onlyNew ?? false}
            onChange={(e) => setFilters((f) => ({ ...f, onlyNew: e.target.checked }))}
          />
          новое с прошлого входа
        </label>
        {Object.values(filters).some(Boolean) && (
          <button className="small ghost" onClick={() => setFilters({})}>сбросить</button>
        )}
      </div>

      {tab === 'доска' && board && (
        <div className="counters">
          <span>всего: <b>{board.counters.total}</b></span>
          {board.counters.rubrics.map((r) => (
            <span key={r.key}>{r.label}: <b>{r.count}</b></span>
          ))}
          {board.counters.directions.length > 0 && <span className="muted">│</span>}
          {board.counters.directions.map((d) => (
            <span key={d.key}>{d.label}: <b>{d.count}</b></span>
          ))}
        </div>
      )}

      {error && <div className="banner"><span className="error">{error}</span></div>}

      {tab === 'доска' ? (
        <Board cards={cards} schema={schema} selected={selected} onSelect={setSelected} />
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          {cards.length === 0 && <p className="empty">ничего нет</p>}
          {tab === 'библиотека'
            ? cards.map((c) => (
                <div key={c.path} className="library-item" onClick={() => setSelected(c.path)}>
                  <div className="row">
                    <strong className="grow">{c.title}</strong>
                    {c.kind === 'пул' && <span className="chip accent">пул тем</span>}
                    {c.isNew && <span className="chip accent">новое</span>}
                  </div>
                  <div className="path">{c.path}</div>
                </div>
              ))
            : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, maxWidth: 940 }}>
                {cards.map((c) => (
                  <div key={c.path} style={{ width: 290 }}>
                    <CardTile card={c} schema={schema} selected={c.path === selected} onSelect={setSelected} />
                  </div>
                ))}
              </div>
            )}
        </div>
      )}

      {selected && (
        <CardPanel
          path={selected}
          schema={schema}
          onClose={() => setSelected(null)}
          onChanged={() => void reload()}
          onOpen={setSelected}
        />
      )}

      {adding && (
        <AddIncoming
          experts={schema.experts}
          onClose={() => setAdding(false)}
          onDone={(path) => { setAdding(false); void reload(); setSelected(path) }}
        />
      )}
    </div>
  )
}

function Select({
  value,
  onChange,
  placeholder,
  options,
}: {
  value: string
  onChange: (v: string) => void
  placeholder: string
  options: Array<[string, string]>
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}: все</option>
      {options.map(([key, label]) => (
        <option key={key} value={key}>{label}</option>
      ))}
    </select>
  )
}

function AddIncoming({
  experts,
  onClose,
  onDone,
}: {
  experts: string[]
  onClose: () => void
  onDone: (path: string) => void
}) {
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [expert, setExpert] = useState(experts[0] ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [warning, setWarning] = useState('')

  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Новое входящее</h3>
        <p className="small muted" style={{ marginTop: 0 }}>
          Транскрибация эфира, чужой пост, ссылка на YouTube конкурента, заметка. Только текст.
        </p>
        <div className="field">
          <label>Заголовок</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <label>Текст</label>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} />
        </div>
        <div className="field">
          <label>Эксперт</label>
          <select value={expert} onChange={(e) => setExpert(e.target.value)}>
            <option value="">— не указан —</option>
            {experts.map((x) => (
              <option key={x} value={x}>{x}</option>
            ))}
          </select>
        </div>
        {warning && <p className="warn small">{warning}</p>}
        {error && <p className="error small">{error}</p>}
        <div className="row">
          <button
            className="primary"
            disabled={busy || !title.trim() || !text.trim()}
            onClick={async () => {
              setBusy(true)
              setError('')
              try {
                const result = await api.createIncoming({ title, text, expert: expert || undefined })
                if (result.warning) {
                  setWarning(result.warning)
                  setBusy(false)
                  return
                }
                onDone(result.path)
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'Не получилось')
                setBusy(false)
              }
            }}
          >
            Добавить
          </button>
          {warning && <button onClick={() => onDone('')}>Понятно</button>}
          <button className="ghost" onClick={onClose}>Отмена</button>
        </div>
      </div>
    </div>
  )
}
